import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter } from "@/components/ui/Card";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import React from "react";
import { useNavigate } from "react-router-dom";
import { default as defaultImg } from "@/assets/default.jpg";
import useTableData from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
import { useGetAllEventQuery } from "@/services/EventApi";

const initialValue = {
  limit: 8,
  offset: 0
}

const { VITE_ORG_ID } = import.meta.env;

const KioskHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { searchCriteria, query, setSearchCriteria } = useTableData(initialValue);

  const {data: eventData, isLoading} = useGetAllEventQuery({ org_id: VITE_ORG_ID, query });

  const {
    data: events = [],
    total_count: totalRecords = 0
  } = eventData ?? {};

  const {
    handleNextPage,
    handlePrevPage,
    isLastPage
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleCardClick = async (cardData: (typeof events)[number]) => {
    navigate(`/kiosk-department/${cardData.event_id}`, {
      state: { event: cardData },
    });
  };

  if (isLoading) 
    return <div className="flex items-center justify-center">Loading...</div>;

  if (!events.length) {
    return (
      <div className="text-center py-6 flex justify-center items-center">
        <h2 className="text-gray-600 text-[1.2rem] font-semibold">
          No Active Events
        </h2>
      </div>
   )
  }

  return (
    <>
      {/* Services Section */}
      <section className="mx-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-0 md:mt-8">
        {events.map((item, index) => (
          <Card
            key={Number(index)}
            onClick={() => handleCardClick(item)}
            className="group relative flex flex-col items-center justify-center w-full py-4 bg-[#EDEDED] hover:cursor-pointer"
          >
            <CardContent className="flex flex-col items-center justify-center text-center leading-0">
              <div className="w-[100%] h-[100%]">
                <img
                  src={defaultImg}
                  alt="Company logo"
                  className="w-[150px] h-[150px] object-contain"
                />
              </div>
            </CardContent>

            <CardFooter className="flex items-center justify-center text-center">
              <p className="capitalize text-[#767676] m-0 group-hover:text-white p-0 font-semibold text-[1.2rem]">
                {item.name}
              </p>
            </CardFooter>
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="text-center px-2">
                <p className="capitalize text-[1.2rem] font-bold m-0">
                  {item.name}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* Pagination Controls */}
      <div className="flex items-center justify-end gap-4 my-4 w-full">
        {searchCriteria.offset !== 0 && <Button
          variant="solid"
          size="icon"
          onClick={handlePrevPage}
          disabled={searchCriteria.offset === 0}
          className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="h-7 w-7" />
        </Button>}

        <Button
          variant="solid"
          size="icon"
          onClick={handleNextPage}
          disabled={isLastPage}
          className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="h-7 w-7" />
        </Button>
      </div>
    </>
  );
};

export default KioskHomePage;
